import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createOfflineStore, type OfflineStore } from './store'
import { registerUpgradePreparer } from './runtime'
import { createSupabaseEntryTransport, getSupabaseClient } from './supabase'
import { clearActiveWallShareUrl } from '../features/sharing/wall-link-storage'
import { SyncEngine } from './sync-engine'
import type { EntryMutationInput, OwnerDocument, SyncSnapshot } from './types'

type OfflineContextValue = {
  snapshot: SyncSnapshot
  online: boolean
  recordEntry(input: EntryMutationInput): Promise<void>
  saveOwnerDocument<T>(document: OwnerDocument<T>): Promise<void>
  getOwnerDocument<T>(ownerId: string): Promise<OwnerDocument<T> | undefined>
  readWorkflowRecovery(key: string): Promise<string | null>
  writeWorkflowRecovery(key: string, value: string | null): Promise<void>
  retry(): Promise<void>
  resumeAfterAuthentication(): void
}

const defaultSnapshot: SyncSnapshot = { phase: 'synced', pending: 0 }
const OfflineContext = createContext<OfflineContextValue | null>(null)

export function OfflineProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState(defaultSnapshot)
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine)
  const storeRef = useRef<OfflineStore | undefined>(undefined)
  const engineRef = useRef<SyncEngine | undefined>(undefined)
  const readyRef = useRef<{
    promise: Promise<{ store: OfflineStore; engine: SyncEngine } | null>
    resolve: (value: { store: OfflineStore; engine: SyncEngine } | null) => void
  } | undefined>(undefined)
  if (!readyRef.current) {
    let resolve!: (value: { store: OfflineStore; engine: SyncEngine } | null) => void
    const promise = new Promise<{ store: OfflineStore; engine: SyncEngine } | null>((done) => { resolve = done })
    readyRef.current = { promise, resolve }
  }

  useEffect(() => {
    let disposed = false
    let unsubscribe: (() => void) | undefined
    let unsubscribeAuth: (() => void) | undefined
    void createOfflineStore().then(async (store) => {
      if (disposed) { store.close(); return }
      storeRef.current = store
      const engine = new SyncEngine(store, createSupabaseEntryTransport())
      engineRef.current = engine
      readyRef.current?.resolve({ store, engine })
      unsubscribe = engine.subscribe(setSnapshot)
      registerUpgradePreparer(() => store.prepareUpgradeCheckpoint())
      await navigator.storage?.persist?.().catch(() => false)
      await engine.initialize(navigator.onLine)
      const client = getSupabaseClient()
      const authListener = client?.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') engine.resumeAfterAuthentication()
        if (event === 'SIGNED_OUT') clearActiveWallShareUrl()
      })
      unsubscribeAuth = () => authListener?.data.subscription.unsubscribe()
    }).catch(() => {
      readyRef.current?.resolve(null)
      setSnapshot({ phase: 'error', pending: 0, message: 'Offline storage is unavailable in this browser.' })
    })
    return () => {
      disposed = true
      unsubscribe?.()
      unsubscribeAuth?.()
      registerUpgradePreparer(undefined)
      storeRef.current?.close()
    }
  }, [])

  useEffect(() => {
    const changed = () => {
      const next = navigator.onLine
      setOnline(next)
      engineRef.current?.setOnline(next)
    }
    window.addEventListener('online', changed)
    window.addEventListener('offline', changed)
    return () => { window.removeEventListener('online', changed); window.removeEventListener('offline', changed) }
  }, [])

  const recordEntry = useCallback(async (input: EntryMutationInput) => {
    const ready = await readyRef.current!.promise
    if (!ready) throw new Error('Offline storage is unavailable in this browser.')
    const client = getSupabaseClient()
    let ownerId = input.ownerId
    if (client) {
      const { data } = await client.auth.getSession()
      if (!data.session?.user.id) {
        setSnapshot({ phase: 'auth-paused', pending: await ready.store.pendingCount(), message: 'Sign in before recording changes in configured staging.' })
        return
      }
      ownerId = data.session.user.id
    }
    await ready.engine.enqueue({ ...input, ownerId })
  }, [])
  const saveOwnerDocument = useCallback(async <T,>(document: OwnerDocument<T>) => {
    const ready = await readyRef.current!.promise
    if (!ready) throw new Error('Offline storage is unavailable in this browser.')
    await ready.store.putOwnerDocument(document)
  }, [])
  const getOwnerDocument = useCallback(async <T,>(ownerId: string) => {
    const ready = await readyRef.current!.promise
    return await ready?.store.getOwnerDocument<T>(ownerId)
  }, [])
  const readWorkflowRecovery = useCallback(async (key: string) => {
    const ready = await readyRef.current!.promise
    if (!ready) return null
    const value = await ready.store.getMetadata(`workflowRecovery:${key}`)
    return typeof value === 'string' ? value : null
  }, [])
  const writeWorkflowRecovery = useCallback(async (key: string, value: string | null) => {
    const ready = await readyRef.current!.promise
    if (!ready) throw new Error('Offline storage is unavailable in this browser.')
    const storageKey = `workflowRecovery:${key}`
    if (value === null) await ready.store.deleteMetadata(storageKey)
    else await ready.store.putMetadata(storageKey, value)
  }, [])
  const retry = useCallback(async () => { await engineRef.current?.flush() }, [])
  const resumeAfterAuthentication = useCallback(() => engineRef.current?.resumeAfterAuthentication(), [])
  const value = useMemo(() => ({ snapshot, online, recordEntry, saveOwnerDocument, getOwnerDocument, readWorkflowRecovery, writeWorkflowRecovery, retry, resumeAfterAuthentication }), [snapshot, online, recordEntry, saveOwnerDocument, getOwnerDocument, readWorkflowRecovery, writeWorkflowRecovery, retry, resumeAfterAuthentication])
  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>
}

export function useOffline() {
  const value = useContext(OfflineContext)
  if (!value) throw new Error('useOffline must be used inside OfflineProvider.')
  return value
}

export function useOnlineRequired() {
  const { online } = useOffline()
  return { online, disabled: !online, reason: online ? undefined : 'This change requires an internet connection.' }
}
